<script lang="ts" setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { useRouter } from 'vue-router'
import { useBountySubmissionStore } from '../stores/bounty-submission'
import { useUserStore } from '@/stores/user/user.store'
import AccountRestrictedBanner from '@/components/AccountRestrictedBanner.vue'

import StepBountyLocation from '../components/bounty/StepBountyLocation.vue'
import StepBountyDateRange from '../components/bounty/StepBountyDateRange.vue'
import StepBountyDetails from '../components/bounty/StepBountyDetails.vue'
import StepBountyReview from '../components/bounty/StepBountyReview.vue'

const $q = useQuasar()
const router = useRouter()
const bountyStore = useBountySubmissionStore()

const { step, isLastStep, isSubmitting, submitError } = storeToRefs(bountyStore)
const { isRestricted } = storeToRefs(useUserStore())

const locationStepRef = ref<InstanceType<typeof StepBountyLocation> | null>(null)
const dateRangeStepRef = ref<InstanceType<typeof StepBountyDateRange> | null>(null)
const detailsStepRef = ref<InstanceType<typeof StepBountyDetails> | null>(null)

const stepRefs = computed(() => ({
  location: locationStepRef,
  dateRange: dateRangeStepRef,
  details: detailsStepRef,
  review: null
}))

const isMobile = computed(() => $q.screen.lt.md)

async function onContinue() {
  const refObj = stepRefs.value[bountyStore.step]
  if (refObj?.value && typeof refObj.value.validate === 'function') {
    const valid = refObj.value.validate()
    if (!valid) return
  }

  if (!isLastStep.value) {
    bountyStore.nextStep()
    return
  }

  const success = await bountyStore.submit()

  if (!success) {
    $q.notify({
      type: 'negative',
      message: submitError.value ?? 'Failed to post bounty. Please try again.',
      position: 'top'
    })
    return
  }

  $q.notify({
    type: 'positive',
    message: 'Bounty posted successfully! You will be notified when your bounty has been approved.',
    position: 'top'
  })
  bountyStore.$reset()
  router.push({ name: 'my-bounties' })
}
</script>

<template>
  <q-page padding class="bg-grey-2">
    <AccountRestrictedBanner v-if="isRestricted" />
    <div v-else class="row layout-wide q-mx-auto">
      <div class="col-12">
        <h4 class="text-h4 text-weight-bold q-mt-none q-mb-sm text-blue-grey-9">Post a Bounty</h4>
        <p class="text-body1 text-blue-grey-8">
          Stake credits to request dashcam footage from a specific location and time.
        </p>
      </div>

      <div class="col-12">
        <q-stepper
          v-model="step"
          color="blue-8"
          animated
          flat
          bordered
          :vertical="isMobile"
          :alternative-labels="!isMobile"
          class="bg-white"
        >
          <q-step
            name="location"
            title="Location"
            icon="mdi-map-marker"
            :done="step !== 'location'"
          >
            <step-bounty-location ref="locationStepRef" />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="isSubmitting"
                @click="onContinue"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            name="dateRange"
            title="Date & Time"
            icon="mdi-calendar-range"
            :done="step === 'details' || step === 'review'"
          >
            <step-bounty-date-range ref="dateRangeStepRef" />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="isSubmitting"
                @click="onContinue"
              />
              <q-btn
                flat
                color="blue-8"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="isSubmitting"
                @click="bountyStore.previousStep()"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step name="details" title="Details" icon="mdi-text-box" :done="step === 'review'">
            <step-bounty-details ref="detailsStepRef" />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="isSubmitting"
                @click="onContinue"
              />
              <q-btn
                flat
                color="blue-8"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="isSubmitting"
                @click="bountyStore.previousStep()"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step name="review" title="Review" icon="mdi-check-circle">
            <step-bounty-review />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                color="blue-8"
                unelevated
                label="Post Bounty"
                class="q-px-lg text-weight-bold"
                :disable="isSubmitting"
                @click="onContinue"
              />
              <q-btn
                flat
                color="blue-8"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="isSubmitting"
                @click="bountyStore.previousStep()"
              />
            </q-stepper-navigation>
          </q-step>
        </q-stepper>
      </div>
    </div>
  </q-page>
</template>
